import { getAuthToken } from "../services/api/http";

export const getJwtToken = () => getAuthToken();

export const isUserAuthenticated = () => {
  const token = getJwtToken();
  if (!token) return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && Date.now() >= payload.exp * 1000) {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error validating token:", error);
    return false;
  }
};

export const initialState = {
  profile: { data: null, loading: true, error: null },
  workspaces: { list: [], loading: true, error: null },
  documents: { list: [], loading: false, error: null },
  recycleBin: { list: [], loading: false, error: null },
  collaborators: { list: [], loading: false, error: null },
  search: { term: "", filteredDocuments: [], filteredWorkspaces: [] },
  ui: {
    previewFile: null,
    showPreviewModal: false,
    selectedWorkspace: null,
    selectedRole: "Viewer",
  },
  loadingStates: {
    profile: false,
    workspaces: false,
    documents: false,
    upload: false,
    collaborators: false,
  },
  auth: {
    hasValidToken: isUserAuthenticated(),
    token: getJwtToken(),
  },
};

export const ACTION_TYPES = {
  SET_PROFILE_LOADING: "SET_PROFILE_LOADING",
  SET_PROFILE_DATA: "SET_PROFILE_DATA",
  SET_PROFILE_ERROR: "SET_PROFILE_ERROR",

  SET_WORKSPACES_LOADING: "SET_WORKSPACES_LOADING",
  SET_WORKSPACES_DATA: "SET_WORKSPACES_DATA",
  SET_WORKSPACES_ERROR: "SET_WORKSPACES_ERROR",
  ADD_WORKSPACE: "ADD_WORKSPACE",
  REMOVE_WORKSPACE: "REMOVE_WORKSPACE",

  SET_DOCUMENTS_LOADING: "SET_DOCUMENTS_LOADING",
  SET_DOCUMENTS_DATA: "SET_DOCUMENTS_DATA",
  SET_DOCUMENTS_ERROR: "SET_DOCUMENTS_ERROR",
  ADD_DOCUMENT: "ADD_DOCUMENT",
  REMOVE_DOCUMENT: "REMOVE_DOCUMENT",
  UPDATE_DOCUMENT: "UPDATE_DOCUMENT",

  SET_RECYCLE_BIN_DATA: "SET_RECYCLE_BIN_DATA",
  REMOVE_FROM_RECYCLE_BIN: "REMOVE_FROM_RECYCLE_BIN",

  SET_COLLABORATORS_LOADING: "SET_COLLABORATORS_LOADING",
  SET_COLLABORATORS_DATA: "SET_COLLABORATORS_DATA",
  SET_COLLABORATORS_ERROR: "SET_COLLABORATORS_ERROR",

  SET_SEARCH_TERM: "SET_SEARCH_TERM",
  SET_FILTERED_DOCUMENTS: "SET_FILTERED_DOCUMENTS",
  SET_FILTERED_WORKSPACES: "SET_FILTERED_WORKSPACES",

  SET_PREVIEW_FILE: "SET_PREVIEW_FILE",
  SET_SHOW_PREVIEW_MODAL: "SET_SHOW_PREVIEW_MODAL",
  SET_SELECTED_WORKSPACE: "SET_SELECTED_WORKSPACE",
  SET_SELECTED_ROLE: "SET_SELECTED_ROLE",

  SET_LOADING_STATE: "SET_LOADING_STATE",
  SET_AUTH_STATE: "SET_AUTH_STATE",
};

export function dashboardReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_PROFILE_LOADING:
      return { ...state, profile: { ...state.profile, loading: action.payload } };

    case ACTION_TYPES.SET_PROFILE_DATA:
      return { ...state, profile: { data: action.payload, loading: false, error: null } };

    case ACTION_TYPES.SET_PROFILE_ERROR:
      return { ...state, profile: { ...state.profile, loading: false, error: action.payload } };

    case ACTION_TYPES.SET_WORKSPACES_LOADING:
      return { ...state, workspaces: { ...state.workspaces, loading: action.payload } };

    case ACTION_TYPES.SET_WORKSPACES_DATA:
      return { ...state, workspaces: { list: action.payload, loading: false, error: null } };

    case ACTION_TYPES.ADD_WORKSPACE:
      return {
        ...state,
        workspaces: { ...state.workspaces, list: [...state.workspaces.list, action.payload] },
      };

    case ACTION_TYPES.REMOVE_WORKSPACE:
      return {
        ...state,
        workspaces: {
          ...state.workspaces,
          list: state.workspaces.list.filter((w) => w._id !== action.payload),
        },
      };

    case ACTION_TYPES.SET_DOCUMENTS_DATA:
      return { ...state, documents: { list: action.payload, loading: false, error: null } };

    case ACTION_TYPES.ADD_DOCUMENT:
      return {
        ...state,
        documents: { ...state.documents, list: [...state.documents.list, action.payload] },
      };

    case ACTION_TYPES.REMOVE_DOCUMENT:
      return {
        ...state,
        documents: {
          ...state.documents,
          list: state.documents.list.filter((doc) => doc._id !== action.payload),
        },
      };

    case ACTION_TYPES.UPDATE_DOCUMENT:
      return {
        ...state,
        documents: {
          ...state.documents,
          list: state.documents.list.map((doc) =>
            doc._id === action.payload.documentId ? action.payload.updatedDocument : doc
          ),
        },
      };

    case ACTION_TYPES.SET_RECYCLE_BIN_DATA:
      return { ...state, recycleBin: { list: action.payload, loading: false, error: null } };

    case ACTION_TYPES.REMOVE_FROM_RECYCLE_BIN:
      return {
        ...state,
        recycleBin: {
          ...state.recycleBin,
          list: state.recycleBin.list.filter((doc) => doc._id !== action.payload),
        },
      };

    case ACTION_TYPES.SET_COLLABORATORS_DATA:
      return { ...state, collaborators: { list: action.payload, loading: false, error: null } };

    case ACTION_TYPES.SET_SEARCH_TERM:
      return { ...state, search: { ...state.search, term: action.payload } };

    case ACTION_TYPES.SET_FILTERED_DOCUMENTS:
      return { ...state, search: { ...state.search, filteredDocuments: action.payload } };

    case ACTION_TYPES.SET_FILTERED_WORKSPACES:
      return { ...state, search: { ...state.search, filteredWorkspaces: action.payload } };

    case ACTION_TYPES.SET_PREVIEW_FILE:
      return { ...state, ui: { ...state.ui, previewFile: action.payload } };

    case ACTION_TYPES.SET_SHOW_PREVIEW_MODAL:
      return { ...state, ui: { ...state.ui, showPreviewModal: action.payload } };

    case ACTION_TYPES.SET_SELECTED_WORKSPACE:
      return { ...state, ui: { ...state.ui, selectedWorkspace: action.payload } };

    case ACTION_TYPES.SET_SELECTED_ROLE:
      return { ...state, ui: { ...state.ui, selectedRole: action.payload } };

    case ACTION_TYPES.SET_LOADING_STATE:
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.payload.key]: action.payload.isLoading },
      };

    case ACTION_TYPES.SET_AUTH_STATE:
      return { ...state, auth: { ...state.auth, ...action.payload } };

    default:
      return state;
  }
}
